import { parse, type Roll, roll, storable } from "./pool";
import { primeDiceSound } from "./sound";

export type FailureReason = "rate_limited" | "refused" | "offline";

// `masked` of a posted roll: the answer's own, what the server stored; absent when the answer does not say.
export type Outcome = { kind: "posted" | "secret" | "local"; roll: Roll; masked?: boolean } | { kind: "failed"; roll: Roll; reason: FailureReason };

// Whose roll it is, as the feed's entry names it (a character or a retainer of the campaign, or a monster, masked as the master asks).
// A monster's roll may name its encounter participant, and the server then masks it too while that monster's token is hidden.
export type Subject = { character_id: string } | { retainer_id: string } | { monster_id: string; masked: boolean; encounter_participant_id?: string };

export type RollRequest = {
	formula: string;
	url: string | null;
	secret: boolean;
	success?: string;
	attack?: boolean;
	description?: string;
	subject?: Subject;
	onRolling?: (roll: Roll) => void;
	fetch?: typeof fetch;
	timeoutMs?: number;
};

// A roll that never answers must not leave its host waiting forever.
const ROLL_TIMEOUT_MS = 15_000;

export function csrfToken() {
	return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
}

function body({ notation, result }: Roll, { success, description, subject }: RollRequest) {
	const form = new FormData();
	form.append("dice_roll[notation]", notation);
	form.append("dice_roll[result]", result);
	if (success) form.append("dice_roll[success]", success);
	if (description) form.append("dice_roll[description]", description);
	for (const [key, value] of Object.entries(subject ?? {})) form.append(`dice_roll[${key}]`, String(value));
	return form;
}

async function storedMask(response: Response): Promise<boolean | undefined> {
	try {
		const { masked } = await response.json();
		return typeof masked === "boolean" ? masked : undefined;
	} catch {
		return undefined;
	}
}

// Parses and rolls a formula, then shares it with the campaign unless it is secret or has no campaign.
// Null when the formula does not parse: nothing was rolled. There is no retry, since a roll is not
// idempotent and a second POST would roll twice.
export async function rollAndPost(request: RollRequest): Promise<Outcome | null> {
	const parsed = parse(request.formula);
	if (!parsed.ok) return null;

	const result = roll(parsed.notation, request.success, request.attack);
	primeDiceSound();
	if (request.secret) return { kind: "secret", roll: result };
	if (request.url === null) return { kind: "local", roll: result };
	// Too long for the log: rolled and shown to the roller only, as the old bar rolled whatever its counters held.
	if (!storable(result)) return { kind: "local", roll: result };

	request.onRolling?.(result);
	const abort = new AbortController();
	const timeout = setTimeout(() => abort.abort(), request.timeoutMs ?? ROLL_TIMEOUT_MS);
	try {
		const response = await (request.fetch ?? fetch)(request.url, {
			method: "POST",
			headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
			body: body(result, request),
			signal: abort.signal,
		});
		if (response.ok) return { kind: "posted", roll: result, masked: await storedMask(response) };
		// A 5xx may have stored the roll before failing.
		return { kind: "failed", roll: result, reason: response.status === 429 ? "rate_limited" : response.status >= 500 ? "offline" : "refused" };
	} catch {
		// An abort lands here too, and reads the same as any other lost response.
		return { kind: "failed", roll: result, reason: "offline" };
	} finally {
		clearTimeout(timeout);
	}
}
